import {
  IconCheck,
  IconEye,
  IconFunction,
  IconPlus,
  IconSettings,
  IconTrash,
  IconWand,
  IconX,
  IconMoodSmile,
  IconCamera,
} from "@tabler/icons-react";
import { memo, useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Accordion, Alert, Badge, Button, Col, Row, Form } from "react-bootstrap";
import { Controller } from "react-hook-form";
import { AVAILABLE_FUNCTIONS } from "../AgentsConstants";
import EmojiPickerWrapper from "../../EmojiPIcker";

const StepFunctions = memo(
  ({ register, watch, setValue, unregister, control, handleAIFill, onGetSchemas }) => {
    // Estado para controlar qual accordion está expandido
    const [activeAccordion, setActiveAccordion] = useState(null);
    const [schemaUpdateTrigger, setSchemaUpdateTrigger] = useState(0);

    // Estados para os emoji pickers dos campos de resposta personalizada
    const [showEmojiPickers, setShowEmojiPickers] = useState({});
    const textareaRefs = useRef({});
    const emojiPickerRefs = useRef({});
    
    // ✅ SIMPLIFICADO: Watch simples para schemas (sem dependência circular)
    useEffect(() => {
      const subscription = watch((value, { name }) => {
        // ✅ Verificar se mudança afeta schemas
        if (
          name &&
          name.includes("functionSchemas") &&
          (name.includes(".enabled") ||
            name.includes(".required") ||
            name.includes(".customFields") ||
            name.includes(".useDefault"))
        ) {
          console.log("🔍 Campo que afeta schema mudou:", name, value[name]);
          setSchemaUpdateTrigger((prev) => prev + 1);
        }
      });

      return () => subscription.unsubscribe();
    }, [watch]);

    // ✅ CORREÇÃO: Função simplificada para inserir emoji (baseada no step3.js)
    const handleEmojiSelect = useCallback(
      (functionId, emoji) => {
        const textarea = textareaRefs.current[functionId];
        if (textarea) {
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          
          // ✅ CORREÇÃO CRÍTICA: Usar o valor atual do textarea em vez do estado local
          // Isso garante que sempre temos o valor mais atualizado
          const currentValue = textarea.value || "";

          // Inserir emoji na posição do cursor
          const newValue =
            currentValue.substring(0, start) +
            emoji +
            currentValue.substring(end);

          // Calcular nova posição do cursor
          const newCursorPosition = start + emoji.length;

          // ✅ CORREÇÃO: Atualizar formulário e DOM de forma síncrona
          setValue(`functionSchemas.${functionId}.customResponse`, newValue, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });

          // ✅ CORREÇÃO: Usar setTimeout com delay mínimo para melhor timing
          setTimeout(() => {
            if (textareaRefs.current[functionId]) {
              // Garantir que o valor foi atualizado no DOM
              textareaRefs.current[functionId].value = newValue;
              textareaRefs.current[functionId].focus();
              textareaRefs.current[functionId].setSelectionRange(
                newCursorPosition,
                newCursorPosition
              );
            }
          }, 0);
        }
      },
      [setValue] // ✅ CORREÇÃO: Remover localTextareaValues das dependências
    );

    // Função para alternar o emoji picker de um campo específico
    const toggleEmojiPicker = useCallback((functionId, e) => {
      e.preventDefault();
      e.stopPropagation();
      setShowEmojiPickers((prev) => ({
        ...prev,
        [functionId]: !prev[functionId],
      }));
    }, []);

    // Fechar emoji picker quando clicar fora
    useEffect(() => {
      const handleClickOutside = (event) => {
        Object.keys(showEmojiPickers).forEach((functionId) => {
          if (
            showEmojiPickers[functionId] &&
            emojiPickerRefs.current[functionId] &&
            !emojiPickerRefs.current[functionId].contains(event.target) &&
            !event.target.closest(`.emoji-picker-button-${functionId}`)
          ) {
            setShowEmojiPickers((prev) => ({
              ...prev,
              [functionId]: false,
            }));
          }
        });
      };

      const hasOpenPickers = Object.values(showEmojiPickers).some(Boolean);
      if (hasOpenPickers) {
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }, [showEmojiPickers]);

    // Fechar todos os emoji pickers quando o componente for desmontado
    useEffect(() => {
      return () => {
        setShowEmojiPickers({});
      };
    }, []);

    // Função para gerar o schema baseado nas configurações
    const generateFunctionSchema = useCallback(
      (functionId) => {
        const func = AVAILABLE_FUNCTIONS.find((f) => f.id === functionId);
        if (!func) return null;

        const functionConfig = watch(`functionSchemas.${functionId}`) || {};
        const schema = {
          name: functionId,
          description: func.description,
          parameters: {
            type: "object",
            properties: {},
            required: [],
          },
        };

        // Adicionar campos habilitados ao schema
        func.fields.forEach((field) => {
          if (functionConfig.fields?.[field.id]?.enabled) {
            const fieldConfig = functionConfig.fields[field.id];

            schema.parameters.properties[field.id] = {
              type: field.type,
              description: field.description,
            };

            // Adicionar enum se aplicável
            if (field.type === "enum" && field.options) {
              schema.parameters.properties[field.id] = {
                type: "string", 
                enum: field.options,
                description: field.description,
              };
            }

            // Adicionar constraint para integer
            if (field.type === "integer" && field.id === "avaliacao_numerica") {
              schema.parameters.properties[field.id].minimum = 1;
              schema.parameters.properties[field.id].maximum = 5;
            }

            // Adicionar aos required se obrigatório
            if (fieldConfig.required) {
              schema.parameters.required.push(field.id);
            }
          }
        });

        // Adicionar campo metadata se existirem campos personalizados
        if (functionConfig.customFields && func.allowCustomFields) {
          const validCustomFields = functionConfig.customFields.filter(
            (customField) => customField.key && customField.value
          );

          if (validCustomFields.length > 0) {
            const metadataProperties = {};
            const metadataRequired = [];

            validCustomFields.forEach((customField) => {
              metadataProperties[customField.key] = {
                type: "string",
                description: customField.value,
              };

              if (customField.required) {
                metadataRequired.push(customField.key);
              }
            });

            schema.parameters.properties.metadata = {
              type: "object",
              description: "Campos personalizados de metadata",
              properties: metadataProperties,
              required: metadataRequired,
            };

            // Se algum campo personalizado for obrigatório, o metadata também é obrigatório
            if (metadataRequired.length > 0) {
              schema.parameters.required.push("metadata");
            }
          }
        }

        return schema;
      },
      [watch]
    );

    // Função para preview do schema
    const [showSchemaPreview, setShowSchemaPreview] = useState(false);
    const allSchemas = useMemo(() => {
      console.log(
        "🔍 DEBUG - Regenerando schemas (trigger)...",
        schemaUpdateTrigger
      );

      const enabled = AVAILABLE_FUNCTIONS.filter((func) =>
        watch(`functionSchemas.${func.id}.enabled`)
      );

      return enabled
        .map((func) => generateFunctionSchema(func.id))
        .filter(Boolean);
    }, [generateFunctionSchema, schemaUpdateTrigger, watch]);

    // Função para gerar os schemas preparados para o backend
    const getGeneratedSchemas = useCallback(() => {
      return allSchemas;
    }, [allSchemas]);

    // Passar a função para o componente pai via useEffect
    useEffect(() => {
      if (onGetSchemas) {
        onGetSchemas(getGeneratedSchemas);
      }
    }, [onGetSchemas, getGeneratedSchemas]);

    // Handler para mudança de accordion
    const handleAccordionSelect = (eventKey) => {
      setActiveAccordion(activeAccordion === eventKey ? null : eventKey);
    };

    // Handler para switch de função
    const handleFunctionToggle = (functionId, enabled) => {
      if (enabled) {
        setValue(`functionSchemas.${functionId}.enabled`, true);
        setActiveAccordion(functionId);
        // Inicializar campos personalizados se não existirem
        if (!watch(`functionSchemas.${functionId}.customFields`)) {
          setValue(`functionSchemas.${functionId}.customFields`, []);
        }
        // Inicializar estrutura de fields se não existir
        if (!watch(`functionSchemas.${functionId}.fields`)) {
          setValue(`functionSchemas.${functionId}.fields`, {});
        }
        // ✅ Inicializar responseMode para coletar_dados se não existir
        if (!watch(`functionSchemas.${functionId}.responseMode`)) {
          setValue(`functionSchemas.${functionId}.responseMode`, "ai_full");
        }
        // ✅ NOVO: Inicializar campos específicos do "atendimento_humano" se não existirem
        if (watch(`functionSchemas.${functionId}.sendCustomResponse`) === undefined) {
          setValue(`functionSchemas.${functionId}.sendCustomResponse`, false);
        }
        if (!watch(`functionSchemas.${functionId}.operatorMessageType`)) {
          setValue(`functionSchemas.${functionId}.operatorMessageType`, "default");
        }
        if (watch(`functionSchemas.${functionId}.operatorMessage`) === undefined) {
          setValue(`functionSchemas.${functionId}.operatorMessage`, "");
        }
        if (watch(`functionSchemas.${functionId}.generateAIMessage`) === undefined) {
          setValue(`functionSchemas.${functionId}.generateAIMessage`, false);
        }
      } else {
        // ✅ CORREÇÃO DEFINITIVA: Desregistrar TODOS os campos da função
        
        // 1. Desregistrar campos principais da função
        unregister(`functionSchemas.${functionId}.registerContactData`);
        unregister(`functionSchemas.${functionId}.registerClientData`);
        
        // 2. Desregistrar todos os campos da função (enabled e required)
        const func = AVAILABLE_FUNCTIONS.find(f => f.id === functionId);
        if (func && func.fields) {
          func.fields.forEach(field => {
            unregister(`functionSchemas.${functionId}.fields.${field.id}.enabled`);
            unregister(`functionSchemas.${functionId}.fields.${field.id}.required`);
          });
        }
        
        // 3. Limpar estrutura completa da função
        setValue(`functionSchemas.${functionId}`, {
          enabled: false,
          useDefault: true,
          customResponse: "",
          responseMode: "ai_full",
          registerClientData: false,
          registerContactData: false,
          fields: {},
          customFields: [],
          // Campos específicos para função "atendimento_humano"
          sendCustomResponse: false,
          operatorMessageType: "default",
          operatorMessage: "",
          generateAIMessage: false,
        });
        
        if (activeAccordion === functionId) {
          setActiveAccordion(null);
        }
      }
    };

    // Funções para gerenciar campos personalizados
    const addCustomField = (functionId) => {
      const currentFields =
        watch(`functionSchemas.${functionId}.customFields`) || [];
      const newField = { key: "", value: "", required: false };
      setValue(`functionSchemas.${functionId}.customFields`, [
        ...currentFields,
        newField,
      ]);
    };

    const removeCustomField = (functionId, index) => {
      const currentFields =
        watch(`functionSchemas.${functionId}.customFields`) || [];
      const newFields = currentFields.filter((_, i) => i !== index);
      setValue(`functionSchemas.${functionId}.customFields`, newFields);
    };

    const updateCustomField = (functionId, index, field, value) => {
      const currentFields =
        watch(`functionSchemas.${functionId}.customFields`) || [];
      const newFields = [...currentFields];
      newFields[index] = { ...newFields[index], [field]: value };
      setValue(`functionSchemas.${functionId}.customFields`, newFields);
    };

    // Validação para campos personalizados
    const validateCustomField = (key, value) => {
      const errors = [];

      // Validar chave
      if (!key || key.trim() === "") {
        errors.push("Chave é obrigatória");
      } else if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key)) {
        errors.push(
          "Chave deve começar com letra ou _ e conter apenas letras, números e _"
        );
      }

      // Validar valor
      if (!value || value.trim() === "") {
        errors.push("Descrição é obrigatória");
      }

      return errors;
    };

    return (
      <div>
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <IconFunction className="me-2 text-primary" size={24} />
            <h4 className="mb-0">Funções e Capacidades</h4>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-info"
              size="sm"
              onClick={() => setShowSchemaPreview(!showSchemaPreview)}
            >
              <IconEye className="me-1" size={14} />
              {showSchemaPreview ? "Ocultar" : "Ver"} Schemas
            </Button>
          </div>
        </div>

        <Form.Group className="mb-4">
          <Form.Check
            type="switch"
            id="functionsEnabled"
            className="ml-4"
            label={
              <span>
                <strong>🎯 Habilitar Funções Inteligentes</strong>
                <br />
                <small className="text-muted">
                  Permite que seu agente execute ações automáticas como agendar,
                  cancelar e muito mais!
                </small>
              </span>
            }
            checked={watch("functionsEnabled") || false}
            onChange={(e) => setValue("functionsEnabled", e.target.checked)}
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Controller
            name="vision_enabled"
            control={control}
            defaultValue={false}
            render={({ field: { onChange, value } }) => (
              <Form.Check
                type="switch"
                id="visionEnabled"
                className="ml-4"
                label={
                  <span>
                    <strong>👁️ Análise de Imagens e PDFs</strong>
                    <br />
                    <small className="text-muted">
                      Permite que o agente analise automaticamente imagens e documentos PDF enviados pelos usuários
                    </small>
                  </span>
                }
                checked={value || false}
                onChange={(e) => onChange(e.target.checked)}
              />
            )}
          />
        </Form.Group>

        {watch("vision_enabled") && (
          <div className="mt-3 ms-3">
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label>
                      <strong>Prompt de Análise Personalizado</strong>
                      <br />
                      <small className="text-muted">
                        Se vazio, usará prompt automático baseado na área do agente
                      </small>
                    </Form.Label>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        const financialPrompt = `Analise este documento financeiro/comprovante. Identifique:
1. Tipo de documento (comprovante de pagamento, transferência, etc.)
2. Valor da transação e moeda
3. Data e horário da transação
4. Instituição financeira ou meio de pagamento
5. Dados do remetente e destinatário (se visíveis)
6. Status da transação (aprovado, pendente, etc.)
7. Número de referência ou código de transação
8. Qualquer informação adicional relevante`;
                        setValue("vision_prompt", financialPrompt);
                      }}
                    >
                      💰 Usar Exemplo Financeiro
                    </Button>
                  </div>
                  
                  <Controller
                    name="vision_prompt"
                    control={control}
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <Form.Control
                        as="textarea"
                        rows={6}
                        value={value || ""}
                        onChange={(e) => {
                          const text = e.target.value;
                          if (text.length <= 1000) {
                            onChange(text);
                          }
                        }}
                        placeholder="Ex: Analise esta imagem e extraia informações sobre produtos, preços e condições..."
                        maxLength={1000}
                      />
                    )}
                  />
                  <Form.Text className="text-muted">
                    {(watch("vision_prompt") || "").length}/1000 caracteres
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>Qualidade da Análise</strong>
                  </Form.Label>
                  <Controller
                    name="vision_detail"
                    control={control}
                    defaultValue="high"
                    render={({ field: { onChange, value } }) => (
                      <div>
                        <Form.Check
                          type="radio"
                          id="vision-detail-high"
                          name="visionDetail"
                          label={
                            <span>
                              <strong className="text-success">🎯 Alta Precisão</strong>
                              <br />
                              <small className="text-muted">
                                Análise detalhada, maior precisão, custo maior
                              </small>
                            </span>
                          }
                          checked={value === "high"}
                          onChange={() => onChange("high")}
                          className="mb-2"
                        />
                        <Form.Check
                          type="radio"
                          id="vision-detail-low"
                          name="visionDetail"
                          label={
                            <span>
                              <strong className="text-warning">⚡ Análise Rápida</strong>
                              <br />
                              <small className="text-muted">
                                Análise básica, menor precisão, custo menor
                              </small>
                            </span>
                          }
                          checked={value === "low"}
                          onChange={() => onChange("low")}
                        />
                      </div>
                    )}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Alert variant="info" className="mt-3">
              <strong>📋 Formatos Suportados:</strong>
              <ul className="mb-0 mt-1">
                <li><strong>Imagens:</strong> JPG, PNG, GIF, WebP</li>
                <li><strong>Documentos:</strong> PDF (máximo 3 páginas)</li>
              </ul>
            </Alert>

            <Alert variant="warning" className="mt-2">
              <small>
                <strong>⚠️ Importante:</strong> A análise visual consome créditos adicionais. 
                Use "Alta Precisão" apenas quando necessário para análises complexas.
              </small>
            </Alert>
          </div>
        )}

        <hr></hr>

        {/* Schema Preview Modal */}
        {showSchemaPreview && (
          <Alert variant="info" className="mb-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0">📋 Preview dos Schemas Gerados</h6>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowSchemaPreview(false)}
              >
                <IconX size={14} />
              </Button>
            </div>
            <pre
              style={{ fontSize: "11px", maxHeight: "300px", overflow: "auto" }}
            >
              {JSON.stringify(allSchemas, null, 2)}
            </pre>
          </Alert>
        )}

        {watch("functionsEnabled") && (
          <div>
            <Alert variant="success" className="mb-4">
              <h6>🚀 Construtor de Funções Inteligentes</h6>
              <p className="mb-2">
                Configure exatamente quais informações cada função deve coletar
                do cliente. É como montar um quebra-cabeça - escolha apenas as
                peças que você precisa!
              </p>
              <small className="text-muted">
                💡 <strong>Dica:</strong> Comece habilitando apenas as funções
                que você realmente usa. Você pode sempre adicionar mais depois.
              </small>
            </Alert>

            {/* Accordion para Funções */}
            <Accordion
              activeKey={activeAccordion}
              onSelect={handleAccordionSelect}
              className="mb-4"
            >
              {AVAILABLE_FUNCTIONS.map((func) => (
                <Accordion.Item eventKey={func.id} key={func.id}>
                  <Accordion.Header>
                    <div className="d-flex align-items-center justify-content-between w-100 me-3">
                      <div className="d-flex align-items-center">
                        <span className="me-3" style={{ fontSize: "20px" }}>
                          {func.icon}
                        </span>
                        <div>
                          <h6 className="mb-0">{func.name}</h6>
                          <small className="">
                            {func.descriptionToUser || func.description}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-5">
                        {watch(`functionSchemas.${func.id}.enabled`) && (
                          <Badge
                            bg="success"
                            className="d-flex align-items-center mt-1"
                          >
                            <IconCheck size={12} className="me-1" />
                            Ativa
                          </Badge>
                        )}
                        <Form.Check
                          type="switch"
                          id={`function-${func.id}`}
                          className="m-0 p-0"
                          label=""
                          checked={watch(`functionSchemas.${func.id}.enabled`) || false}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleFunctionToggle(func.id, e.target.checked);
                          }}
                        />
                      </div>
                    </div>
                  </Accordion.Header>

                  <Accordion.Body>
                    {watch(`functionSchemas.${func.id}.enabled`) && (
                      <div>
                        {func.fields.length > 0 ? (
                          <div>
                            <div className="mb-3">
                              <small className="text-muted">
                                🎛️ <strong>Selecione os campos</strong> que o
                                agente deve coletar:
                              </small>
                            </div>

                            <Row>
                              {func.fields.map((field) => (
                                <Col md={6} key={field.id} className="mb-3">
                                  <div className="border rounded p-3 h-100 bg-light">
                                    <div className="d-flex align-items-start justify-content-between mb-2">
                                      <div className="flex-grow-1">
                                        <div className="d-flex align-items-center mb-1">
                                          <Controller
                                            name={`functionSchemas.${func.id}.fields.${field.id}.enabled`}
                                            control={control}
                                            defaultValue={false}
                                            render={({ field: controllerField }) => (
                                              <Form.Check
                                                type="switch"
                                                id={`field-${func.id}-${field.id}`}
                                                className="ml-3"
                                                label={
                                                  <strong>{field.label}</strong>
                                                }
                                                checked={controllerField.value || false}
                                                onChange={(e) => controllerField.onChange(e.target.checked)}
                                              />
                                            )}
                                          />
                                          <Badge
                                            bg={
                                              field.type === "string"
                                                ? "info"
                                                : field.type === "boolean"
                                                ? "warning"
                                                : field.type === "enum"
                                                ? "success"
                                                : "secondary"
                                            }
                                            className="ms-2"
                                            style={{ fontSize: "10px" }}
                                          >
                                            {field.type}
                                          </Badge>
                                        </div>
                                        <small className="text-muted">
                                          {field.descriptionToUser || field.description}
                                        </small>

                                        {field.type === "enum" &&
                                          field.options && (
                                            <div className="mt-1">
                                              <small className="text-info">
                                                📝 Opções:{" "}
                                                {field.options.join(", ")}
                                              </small>
                                            </div>
                                          )}
                                      </div>
                                    </div>

                                    {watch(
                                      `functionSchemas.${func.id}.fields.${field.id}.enabled`
                                    ) && (
                                      <div className="mt-2 ps-3 border-start border-primary">
                                        <Controller
                                          name={`functionSchemas.${func.id}.fields.${field.id}.required`}
                                          control={control}
                                          defaultValue={false}
                                          render={({ field: controllerField }) => (
                                            <Form.Check
                                              type="checkbox"
                                              id={`required-${func.id}-${field.id}`}
                                              label={
                                                <span>
                                                  <strong className="text-danger">
                                                    Campo Obrigatório
                                                  </strong>
                                                  <br />
                                                  <small className="text-muted">
                                                    Se marcado, o agente SEMPRE
                                                    pedirá esta informação
                                                  </small>
                                                </span>
                                              }
                                              checked={controllerField.value || false}
                                              onChange={(e) => controllerField.onChange(e.target.checked)}
                                            />
                                          )}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              ))}
                            </Row>
                          </div>
                        ) : (
                          <Alert variant="info" className="mb-3">
                            <small>
                              🎯 Esta função não possui campos predefinidos.
                            </small>
                          </Alert>
                        )}

                        {/* Seção de Campos Personalizados */}
                        {func.allowCustomFields && (
                          <div className="mt-4 pt-3 border-top">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <h6 className="mb-0">
                                🔧 Campos Personalizados (Metadata)
                              </h6>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => addCustomField(func.id)}
                              >
                                <IconPlus className="me-1" size={14} />
                                Adicionar Campo
                              </Button>
                            </div>

                            <Alert variant="warning" className="mb-3">
                              <small>
                                <strong>
                                  📋 Formato dos Campos Personalizados:
                                </strong>
                                <br />• <strong>Chave:</strong> nome_do_campo
                                (sem espaços, use _ para separar palavras)
                                <br />• <strong>Descrição:</strong> Descrição
                                clara do que o campo representa
                                <br />
                                <br />
                                <strong>Exemplos:</strong>
                                <br />• <code>area_atuacao</code> → "Segmento de
                                negócio do cliente"
                                <br />• <code>volume_atendimento</code> →
                                "Número aproximado de conversas/dia"
                                <br />• <code>fonte_leads</code> → "Principal
                                origem dos leads"
                              </small>
                            </Alert>

                            {watch(
                              `functionSchemas.${func.id}.customFields`
                            )?.map((customField, index) => {
                              const fieldErrors = validateCustomField(
                                customField.key,
                                customField.value
                              );
                              const hasErrors = fieldErrors.length > 0;

                              return (
                                <div
                                  key={index}
                                  className={`border rounded p-3 mb-3 ${
                                    hasErrors
                                      ? "border-danger"
                                      : "border-secondary"
                                  }`}
                                >
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    <h6 className="mb-0">
                                      Campo Personalizado #{index + 1}
                                    </h6>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() =>
                                        removeCustomField(func.id, index)
                                      }
                                    >
                                      <IconTrash size={14} />
                                    </Button>
                                  </div>

                                  <Row>
                                    <Col md={4}>
                                      <Form.Group className="mb-2">
                                        <Form.Label>Chave *</Form.Label>
                                        <Form.Control
                                          type="text"
                                          value={customField.key || ""}
                                          onChange={(e) =>
                                            updateCustomField(
                                              func.id,
                                              index,
                                              "key",
                                              e.target.value
                                            )
                                          }
                                          placeholder="nome_campo"
                                          isInvalid={
                                            hasErrors &&
                                            (!customField.key ||
                                              !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(
                                                customField.key
                                              ))
                                          }
                                        />
                                        <Form.Text className="text-muted">
                                          Use apenas letras, números e _
                                        </Form.Text>
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group className="mb-2">
                                        <Form.Label>Descrição *</Form.Label>
                                        <Form.Control
                                          type="text"
                                          value={customField.value || ""}
                                          onChange={(e) =>
                                            updateCustomField(
                                              func.id,
                                              index,
                                              "value",
                                              e.target.value
                                            )
                                          }
                                          placeholder="Descrição do que o campo representa"
                                          isInvalid={
                                            hasErrors &&
                                            (!customField.value ||
                                              customField.value.trim() === "")
                                          }
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                      <Form.Group className="mb-2">
                                        <Form.Label>Obrigatório</Form.Label>
                                        <Form.Check
                                          type="checkbox"
                                          checked={
                                            customField.required || false
                                          }
                                          onChange={(e) =>
                                            updateCustomField(
                                              func.id,
                                              index,
                                              "required",
                                              e.target.checked
                                            )
                                          }
                                        />
                                      </Form.Group>
                                    </Col>
                                  </Row>

                                  {hasErrors && (
                                    <Alert
                                      variant="danger"
                                      className="mb-0 mt-2"
                                    >
                                      <small>
                                        <strong>Erros:</strong>
                                        <ul className="mb-0 mt-1">
                                          {fieldErrors.map(
                                            (error, errorIndex) => (
                                              <li key={errorIndex}>{error}</li>
                                            )
                                          )}
                                        </ul>
                                      </small>
                                    </Alert>
                                  )}
                                </div>
                              );
                            })}

                            {(!watch(
                              `functionSchemas.${func.id}.customFields`
                            ) ||
                              watch(`functionSchemas.${func.id}.customFields`)
                                .length === 0) && (
                              <div className="text-center py-3 text-muted">
                                <IconSettings size={32} className="mb-2" />
                                <p className="mb-0">
                                  Nenhum campo personalizado adicionado.
                                </p>
                                <small>
                                  Clique em "Adicionar Campo" para criar campos
                                  de metadata personalizados.
                                </small>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Opções de registro - ocultadas se showRegisterOptions === false */}
                        {func.showRegisterOptions !== false && (
                          <>
                            <div className="mt-4 pt-3 border-top">
                              <Controller
                                name={`functionSchemas.${func.id}.registerContactData`}
                                control={control}
                                defaultValue={false}
                                render={({ field: controllerField }) => (
                                  <Form.Check
                                    type="switch"
                                    id={`registerCRM-${func.id}`}
                                    label="Cadastrar no CRM"
                                    className="mb-3 ml-3"
                                    checked={controllerField.value || false}
                                    onChange={(e) => controllerField.onChange(e.target.checked)}
                                  />
                                )}
                              />
                            </div>
                            <div className="mt-4 pt-3 border-top">
                              <Controller
                                name={`functionSchemas.${func.id}.registerClientData`}
                                control={control}
                                defaultValue={false}
                                render={({ field: controllerField }) => (
                                  <Form.Check
                                    type="switch"
                                    id={`registerClient-${func.id}`}
                                    label="Cadastrar Cliente"
                                    className="mb-3 ml-3"
                                    checked={controllerField.value || false}
                                    onChange={(e) => controllerField.onChange(e.target.checked)}
                                  />
                                )}
                              />
                            </div>
                          </>
                        )}

                        {/* Resposta Personalizada */}
                        {func.hasResponse && (
                          <div className="mt-4 pt-3 border-top">
                            <h6 className="mb-3">💬 Resposta do Agente</h6>

                            {/* Lógica para funções silenciosas por padrão (ex: atendimento_humano) */}
                            {func.silentByDefault ? (
                              <>
                                <Controller
                                  name={`functionSchemas.${func.id}.sendCustomResponse`}
                                  control={control}
                                  defaultValue={false}
                                  render={({ field: { onChange, value } }) => {
                                    const checkedValue = value === true || value === "true";
                                    return (
                                      <Form.Check
                                        type="switch"
                                        id={`sendCustomResponse-${func.id}`}
                                        label="Enviar mensagem personalizada ao cliente"
                                        className="mb-3 ml-3"
                                        checked={checkedValue}
                                        onChange={(e) => onChange(e.target.checked)}
                                      />
                                    );
                                  }}
                                />

                                {watch(`functionSchemas.${func.id}.sendCustomResponse`) ? (
                                  <Form.Group>
                                    <div className="d-flex justify-content-between mb-1">
                                      <Form.Label>
                                        Resposta Personalizada
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          className="ms-2"
                                          onClick={() =>
                                            handleAIFill(
                                              `functionSchemas.${func.id}.customResponse`,
                                              {
                                                label: `Resposta para ${func.name}`,
                                                description: `Resposta que o agente dará após executar ${func.name.toLowerCase()}`,
                                              }
                                            )
                                          }
                                        >
                                          <IconWand className="me-1" size={14} />
                                          IA
                                        </Button>
                                      </Form.Label>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className={`emoji-picker-button-${func.id} p-1`}
                                        onClick={(e) => toggleEmojiPicker(func.id, e)}
                                        type="button"
                                      >
                                        <IconMoodSmile size={16} />
                                      </Button>
                                    </div>

                                    <div style={{ position: "relative" }}>
                                      <Controller
                                        name={`functionSchemas.${func.id}.customResponse`}
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                          <Form.Control
                                            {...field}
                                            as="textarea"
                                            rows={3}
                                            placeholder="Ex: Vou transferir voce para um de nossos atendentes. Aguarde um momento..."
                                            ref={(el) => {
                                              field.ref(el);
                                              if (el) {
                                                textareaRefs.current[func.id] = el;
                                              }
                                            }}
                                          />
                                        )}
                                      />

                                      {showEmojiPickers[func.id] && (
                                        <div
                                          ref={(el) => {
                                            if (el) {
                                              emojiPickerRefs.current[func.id] = el;
                                            }
                                          }}
                                          style={{
                                            position: "absolute",
                                            top: "100%",
                                            right: 0,
                                            zIndex: 1050,
                                            marginTop: "5px",
                                          }}
                                        >
                                          <EmojiPickerWrapper
                                            onEmojiSelect={(emoji) => handleEmojiSelect(func.id, emoji)}
                                            onClose={() =>
                                              setShowEmojiPickers((prev) => ({
                                                ...prev,
                                                [func.id]: false,
                                              }))
                                            }
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </Form.Group>
                                ) : (
                                  <Alert variant="warning" className="mb-0">
                                    <small>
                                      🔇 Nenhuma mensagem sera enviada ao cliente.
                                      O agente apenas acionara o atendimento humano silenciosamente.
                                    </small>
                                  </Alert>
                                )}

                                {/* Mensagem ao Operador */}
                                <div className="mt-4 pt-3 border-top">
                                  <h6 className="mb-3">📋 Mensagem ao Operador</h6>

                                  <Controller
                                    name={`functionSchemas.${func.id}.operatorMessageType`}
                                    control={control}
                                    defaultValue="default"
                                    render={({ field: { onChange, value } }) => (
                                      <div className="mb-3">
                                        <Form.Check
                                          type="radio"
                                          id={`operatorMsg-default-${func.id}`}
                                          name={`operatorMessageType-${func.id}`}
                                          label={
                                            <span>
                                              <strong>Mensagem padrao</strong>
                                              <br />
                                              <small className="text-muted">
                                                O operador recebera a notificacao padrao de transferencia
                                              </small>
                                            </span>
                                          }
                                          checked={value === "default" || !value}
                                          onChange={() => onChange("default")}
                                          className="mb-2"
                                        />
                                        <Form.Check
                                          type="radio"
                                          id={`operatorMsg-custom-${func.id}`}
                                          name={`operatorMessageType-${func.id}`}
                                          label={
                                            <span>
                                              <strong>Mensagem personalizada</strong>
                                              <br />
                                              <small className="text-muted">
                                                Defina uma mensagem customizada para o operador
                                              </small>
                                            </span>
                                          }
                                          checked={value === "custom"}
                                          onChange={() => onChange("custom")}
                                          className="mb-2"
                                        />
                                        <Form.Check
                                          type="radio"
                                          id={`operatorMsg-ai-${func.id}`}
                                          name={`operatorMessageType-${func.id}`}
                                          label={
                                            <span>
                                              <strong>Mensagem com IA</strong>
                                              <br />
                                              <small className="text-muted">
                                                A IA gera um resumo baseado nas ultimas 20 mensagens
                                              </small>
                                            </span>
                                          }
                                          checked={value === "ai"}
                                          onChange={() => onChange("ai")}
                                          className="mb-2"
                                        />
                                      </div>
                                    )}
                                  />

                                  {watch(`functionSchemas.${func.id}.operatorMessageType`) === "custom" && (
                                    <Form.Group className="ms-4">
                                      <Form.Label>Mensagem ao Operador</Form.Label>
                                      <Controller
                                        name={`functionSchemas.${func.id}.operatorMessage`}
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                          <>
                                            <Form.Control
                                              {...field}
                                              as="textarea"
                                              rows={2}
                                              maxLength={120}
                                              placeholder="Ex: Cliente solicitou atendimento humano."
                                              onChange={(e) => {
                                                if (e.target.value.length <= 120) {
                                                  field.onChange(e.target.value);
                                                }
                                              }}
                                            />
                                            <div className="d-flex justify-content-between">
                                              <Form.Text className="text-muted">
                                                {(field.value || "").length}/120 caracteres
                                              </Form.Text>
                                              <Form.Text className="text-muted">
                                                💡 Use {"{{telefone}}"} para dados dinâmicos
                                              </Form.Text>
                                            </div>
                                          </>
                                        )}
                                      />
                                    </Form.Group>
                                  )}

                                  {watch(`functionSchemas.${func.id}.operatorMessageType`) === "ai" && (
                                    <Alert variant="success" className="mb-0 ms-4">
                                      <small>
                                        🤖 A IA gerara automaticamente um resumo baseado nas ultimas 20 mensagens
                                        da conversa para contextualizar o operador sobre o atendimento.
                                      </small>
                                    </Alert>
                                  )}
                                </div>
                              </>
                            ) : func.id === "coletar_dados" ? (
                              <>
                                {/* Lógica especial para coletar_dados: 3 modos de resposta */}
                                <Controller
                                  name={`functionSchemas.${func.id}.responseMode`}
                                  control={control}
                                  defaultValue="ai_full"
                                  render={({ field: { onChange, value } }) => (
                                    <div className="mb-3">
                                      <Form.Check
                                        type="radio"
                                        id={`responseMode-fixed-${func.id}`}
                                        name={`responseMode-${func.id}`}
                                        label={
                                          <span>
                                            <strong>Resposta fixa</strong>
                                            <br />
                                            <small className="text-muted">
                                              Envia a mensagem personalizada exatamente como definida, sem passar pela IA
                                            </small>
                                          </span>
                                        }
                                        checked={value === "fixed"}
                                        onChange={() => onChange("fixed")}
                                        className="mb-2"
                                      />
                                      <Form.Check
                                        type="radio"
                                        id={`responseMode-ai_full-${func.id}`}
                                        name={`responseMode-${func.id}`}
                                        label={
                                          <span>
                                            <strong>Resposta com IA (contexto completo)</strong>
                                            <br />
                                            <small className="text-muted">
                                              A IA gera a resposta com o contexto completo e continua seguindo o prompt normalmente
                                            </small>
                                          </span>
                                        }
                                        checked={value === "ai_full" || !value}
                                        onChange={() => onChange("ai_full")}
                                        className="mb-2"
                                      />
                                    </div>
                                  )}
                                />

                                {/* Textarea para resposta fixa */}
                                {watch(`functionSchemas.${func.id}.responseMode`) === "fixed" && (
                                  <Form.Group>
                                    <div className="d-flex justify-content-between mb-1">
                                      <Form.Label>
                                        Resposta Personalizada
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          className="ms-2"
                                          onClick={() =>
                                            handleAIFill(
                                              `functionSchemas.${func.id}.customResponse`,
                                              {
                                                label: `Resposta para ${func.name}`,
                                                description: `Resposta que o agente dará após executar ${func.name.toLowerCase()}`,
                                              }
                                            )
                                          }
                                        >
                                          <IconWand className="me-1" size={14} />
                                          IA
                                        </Button>
                                      </Form.Label>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className={`emoji-picker-button-${func.id} p-1`}
                                        onClick={(e) => toggleEmojiPicker(func.id, e)}
                                        type="button"
                                      >
                                        <IconMoodSmile size={16} />
                                      </Button>
                                    </div>

                                    <div style={{ position: "relative" }}>
                                      <Controller
                                        name={`functionSchemas.${func.id}.customResponse`}
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                          <Form.Control
                                            {...field}
                                            as="textarea"
                                            rows={3}
                                            placeholder="Ex: Obrigado, {{nome}}! Seus dados foram salvos com sucesso."
                                            ref={(el) => {
                                              field.ref(el);
                                              if (el) {
                                                textareaRefs.current[func.id] = el;
                                              }
                                            }}
                                          />
                                        )}
                                      />

                                      {showEmojiPickers[func.id] && (
                                        <div
                                          ref={(el) => {
                                            if (el) {
                                              emojiPickerRefs.current[func.id] = el;
                                            }
                                          }}
                                          style={{
                                            position: "absolute",
                                            top: "100%",
                                            right: 0,
                                            zIndex: 1050,
                                            marginTop: "5px",
                                          }}
                                        >
                                          <EmojiPickerWrapper
                                            onEmojiSelect={(emoji) => handleEmojiSelect(func.id, emoji)}
                                            onClose={() =>
                                              setShowEmojiPickers((prev) => ({
                                                ...prev,
                                                [func.id]: false,
                                              }))
                                            }
                                          />
                                        </div>
                                      )}
                                    </div>

                                    <Form.Text className="text-muted">
                                      💡 Use códigos como {"{{nome}}"}, {"{{data}}"}
                                      , {"{{hora}}"} para dados dinâmicos
                                    </Form.Text>

                                    <Alert variant="warning" className="mb-0 mt-2">
                                      <small>
                                        Esta mensagem será enviada exatamente como escrita. Normalmente utilizada ao final do prompt para encerrar o atendimento com a coleta.
                                      </small>
                                    </Alert>
                                  </Form.Group>
                                )}

                                {/* Info para ai_full */}
                                {(watch(`functionSchemas.${func.id}.responseMode`) === "ai_full" || !watch(`functionSchemas.${func.id}.responseMode`)) && (
                                  <Alert variant="success" className="mb-0">
                                    <small>
                                      🧠 A IA gerará a resposta com o contexto completo da conversa (prompt, histórico e RAG) e continuará seguindo o fluxo do prompt normalmente. Ideal para coleta de dados no meio da conversa.
                                    </small>
                                  </Alert>
                                )}
                              </>
                            ) : (
                              <>
                                {/* Lógica padrão para outras funções */}
                                <Controller
                                  name={`functionSchemas.${func.id}.useDefault`}
                                  control={control}
                                  render={({ field: { onChange, value } }) => {
                                    const checkedValue = value === true || value === "true";
                                    return (
                                      <Form.Check
                                        type="switch"
                                        id={`useDefault-${func.id}`}
                                        label="Usar resposta automática do sistema"
                                        className="mb-3 ml-3"
                                        checked={checkedValue}
                                        onChange={(e) => onChange(e.target.checked)}
                                      />
                                    );
                                  }}
                                />

                                {!watch(`functionSchemas.${func.id}.useDefault`) && (
                                  <Form.Group>
                                    <div className="d-flex justify-content-between mb-1">
                                      <Form.Label>
                                        Resposta Personalizada
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          className="ms-2"
                                          onClick={() =>
                                            handleAIFill(
                                              `functionSchemas.${func.id}.customResponse`,
                                              {
                                                label: `Resposta para ${func.name}`,
                                                description: `Resposta que o agente dará após executar ${func.name.toLowerCase()}`,
                                              }
                                            )
                                          }
                                        >
                                          <IconWand className="me-1" size={14} />
                                          IA
                                        </Button>
                                      </Form.Label>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className={`emoji-picker-button-${func.id} p-1`}
                                        onClick={(e) => toggleEmojiPicker(func.id, e)}
                                        type="button"
                                      >
                                        <IconMoodSmile size={16} />
                                      </Button>
                                    </div>

                                    <div style={{ position: "relative" }}>
                                      <Controller
                                        name={`functionSchemas.${func.id}.customResponse`}
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                          <Form.Control
                                            {...field}
                                            as="textarea"
                                            rows={3}
                                            placeholder={`O que o agente deve responder após ${func.name.toLowerCase()}?`}
                                            ref={(el) => {
                                              field.ref(el);
                                              if (el) {
                                                textareaRefs.current[func.id] = el;
                                              }
                                            }}
                                          />
                                        )}
                                      />

                                      {showEmojiPickers[func.id] && (
                                        <div
                                          ref={(el) => {
                                            if (el) {
                                              emojiPickerRefs.current[func.id] = el;
                                            }
                                          }}
                                          style={{
                                            position: "absolute",
                                            top: "100%",
                                            right: 0,
                                            zIndex: 1050,
                                            marginTop: "5px",
                                          }}
                                        >
                                          <EmojiPickerWrapper
                                            onEmojiSelect={(emoji) => handleEmojiSelect(func.id, emoji)}
                                            onClose={() =>
                                              setShowEmojiPickers((prev) => ({
                                                ...prev,
                                                [func.id]: false,
                                              }))
                                            }
                                          />
                                        </div>
                                      )}
                                    </div>

                                    <Form.Text className="text-muted">
                                      💡 Use códigos como {"{{nome}}"}, {"{{data}}"}
                                      , {"{{hora}}"} para dados dinâmicos
                                    </Form.Text>
                                  </Form.Group>
                                )}

                                {watch(`functionSchemas.${func.id}.useDefault`) && (
                                  <Alert variant="success" className="mb-0">
                                    <small>
                                      ✅ O sistema criará automaticamente uma
                                      resposta profissional com os dados
                                      preenchidos!
                                    </small>
                                  </Alert>
                                )}
                              </>
                            )}
                          </div>
                        )}

                        {!func.hasResponse && (
                          <Alert variant="info" className="mt-3">
                            <small>
                              🔧 Esta é uma função interna - retorna uma
                              resposta estruturada do sistema.
                            </small>
                          </Alert>
                        )}
                      </div>
                    )}

                    {!watch(`functionSchemas.${func.id}.enabled`) && (
                      <div className="text-center py-4 text-muted">
                        <IconSettings size={32} className="mb-2" />
                        <p className="mb-0">
                          Ative esta função para configurar seus parâmetros
                        </p>
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>

            {/* Resumo das Funções Ativas */}
            {AVAILABLE_FUNCTIONS.some((func) =>
              watch(`functionSchemas.${func.id}.enabled`)
            ) && (
              <Alert variant="primary" className="mt-4">
                <h6>📊 Resumo das Suas Funções</h6>
                <Row>
                  {AVAILABLE_FUNCTIONS.filter((func) =>
                    watch(`functionSchemas.${func.id}.enabled`)
                  ).map((func) => {
                    const enabledFields = func.fields.filter((field) =>
                      watch(
                        `functionSchemas.${func.id}.fields.${field.id}.enabled`
                      )
                    );
                    const requiredFields = enabledFields.filter((field) =>
                      watch(
                        `functionSchemas.${func.id}.fields.${field.id}.required`
                      )
                    );
                    const customFields =
                      watch(`functionSchemas.${func.id}.customFields`) || [];
                    const validCustomFields = customFields.filter(
                      (cf) => cf.key && cf.value
                    );

                    return (
                      <Col md={6} key={func.id} className="mb-2">
                        <div className="d-flex align-items-center">
                          <span className="me-2">{func.icon}</span>
                          <div>
                            <strong>{func.name}</strong>
                            <br />
                            <small className="text-muted">
                              {enabledFields.length} campos padrão •{" "}
                              {validCustomFields.length} personalizados
                              <br />
                              {requiredFields.length +
                                validCustomFields.filter((cf) => cf.required)
                                  .length}{" "}
                              obrigatórios
                            </small>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Alert>
            )}
          </div>
        )}

      </div>
    );
  }
);

StepFunctions.displayName = "StepFunctions";

export default StepFunctions;
